import { createContext, type Dispatch, type SetStateAction, useContext } from 'react';

import { IconButton } from '../Button';
import { LayerNameCell, type LayerNameCellProps } from './LayerNameCell';
import { LayerRowActionCell } from './LayerRowActionCell';
import type { LayerInfo, LayerLibraryRow } from './types';

type LayerTableCellContextValue = {
  openInfoRowId: string | null;
  setOpenInfoRowId: Dispatch<SetStateAction<string | null>>;
  getLayerInfo: (row: LayerLibraryRow) => LayerInfo;
  onRowAction?: (row: LayerLibraryRow) => void;
};

// Cells read per-render state from here so column definitions stay constant: TanStack renders
// each `cell` function as a component, so a new one remounts every cell and an open popover's trigger.
const LayerTableCellContext = createContext<LayerTableCellContextValue | null>(null);

function useLayerTableCellContext() {
  const context = useContext(LayerTableCellContext);
  if (!context) throw new Error('Layer table cells must render inside LayerTableCellContext.Provider');
  return context;
}

function LayerNameInfoCell({ row, infoSide }: { row: LayerLibraryRow; infoSide?: LayerNameCellProps['infoSide'] }) {
  const { openInfoRowId, setOpenInfoRowId, getLayerInfo } = useLayerTableCellContext();
  return (
    <LayerNameCell
      name={row.name}
      layerKind={row.layerKind}
      layerKindColor={row.layerKindColor}
      layerKindOutlineColor={row.layerKindOutlineColor}
      info={getLayerInfo(row)}
      infoOpen={openInfoRowId === row.id}
      onInfoOpenChange={(open) => setOpenInfoRowId(open ? row.id : null)}
      infoSide={infoSide}
      infoTestId={`layer-name-info-${row.id}`}
    />
  );
}

function LayerAddCell({ row }: { row: LayerLibraryRow }) {
  const { onRowAction } = useLayerTableCellContext();
  return <LayerRowActionCell data-testid={`layer-row-add-${row.id}`} onAdd={() => onRowAction?.(row)} />;
}

function LayerRemoveCell({ row }: { row: LayerLibraryRow }) {
  const { onRowAction } = useLayerTableCellContext();
  return (
    <IconButton
      icon="trash"
      variant="danger"
      size="icon"
      className="size-6"
      disabled={!onRowAction}
      onClick={() => onRowAction?.(row)}
      aria-label={`Remove ${row.name}`}
      data-testid={`selected-layer-remove-${row.id}`}
    />
  );
}

export { LayerAddCell, LayerNameInfoCell, LayerRemoveCell, LayerTableCellContext };
